import { productService, reviewService } from "@cabukaka/services";
import { Store, MapPin, Star, Phone, ShoppingBag, Info } from "lucide-react";
import PageHead from "../../../components/ui/PageHead";
import { ProductsList } from "../../../components/shared/products/ProductsList";
import { useAuth, useShop, useToken } from "@cabukaka/hooks";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ReviewModal from "../../../components/shared/reviews/ReviewModal";
import { alertError } from "../../../helpers/alertError";

export default function ShopPage() {
    const { id } = useParams();
    const [products, setProducts] = useState<any[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [activeTab, setActiveTab] = useState<"products" | "about">("products");
    const { shop } = useShop({ id: id! });
    const [reviewModal, setReviewModal] = useState(false);
    const [stats, setStats] = useState<{ rating: number; reviews: number } | null>(null);
    const { user } = useAuth();
    const { token } = useToken();
    

    const handleSubmit = async ({ comment, rating }: { comment: string, rating: number }) => {
        if (!user || !token) {
            alertError("Veuillez vous connecter pour donner votre avis");
            return;
        }
        try {
            await reviewService.baseService.create({
                rating, comment, shopId: id, object: "shop",
                client: {
                    name: user?.username,
                    phone: user.profile?.phone || "",
                    email: user.email
                }
            }, token);
            
            // Rafraîchir les statistiques en direct après une soumission réussie
            const updatedStats = await reviewService.getShopStats(id!);
            setStats(updatedStats);
            setReviewModal(false);
        } catch (error) {
            console.error(error);
        }
    };

    // Charger les produits de la boutique
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await productService.getProductsByShopId(id!);
                setProducts(response?.data || []);
            } catch (error) {
                console.error(error);
            } finally {
                setLoadingProducts(false);
            }
        };
        fetchProducts();
    }, [id]);

    // Charger les vraies statistiques des avis en direct
    useEffect(() => {
        const fetchShopStats = async () => {
            try {
                const data = await reviewService.getShopStats(id!);
                setStats(data);
            } catch (error) {
                console.error(error);
            }
        };
        if (id) fetchShopStats();
    }, [id]);

    if (!shop && !loadingProducts) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                <div className="text-center max-w-sm bg-white p-6 rounded-xl shadow-sm">
                    <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-gray-900">Boutique introuvable</h2>
                    <p className="text-gray-500 mt-1 text-xs">Ce vendeur n'existe pas ou sa boutique a été désactivée.</p>
                </div>
            </div>
        );
    }

    if (!shop) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent"></div>
            </div>
        );
    }

    // Fallback : On prend les stats de l'API, sinon celles embarquées dans l'objet shop, sinon 0
    const currentRating = stats ? stats.rating : (shop.rating || 0);
    const currentReviewsCount = stats ? stats.reviews : (shop.reviews || 0);

    return (
        <div className="min-h-screen bg-gray-100">
            <PageHead title={`${shop.name} | Cabukaka`} description={shop.description} />

            {/* Bannière simplifiée et moins haute pour maximiser l'espace commercial */}
            <div className="relative h-28 md:h-40 bg-gray-800">
                {shop.coverImage && (
                    <img src={shop.coverImage} alt={shop.name} className="w-full h-full object-cover opacity-60" />
                )}
            </div>

            {/* Fiche Vendeur rétractée / Style Kiosque */}
            <div className="container mx-auto px-2 sm:px-4">
                <div className="bg-white rounded-b-xl shadow-sm p-4 relative z-10 -mt-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            {/* Logo compact */}
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-white border-2 border-gray-100 flex-shrink-0 shadow-sm">
                                {shop.logo ? (
                                    <img src={shop.logo} alt={shop.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full bg-orange-50 flex items-center justify-center">
                                        <Store className="w-8 h-8 text-orange-500" />
                                    </div>
                                )}
                            </div>
                            
                            {/* Métadonnées cruciales de confiance (Note / Ville) */}
                            <div>
                                <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{shop.name}</h1>
                                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                                    <div className="flex items-center gap-0.5 text-amber-600 font-semibold">
                                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                                        <span>{currentRating}</span>
                                        <span className="text-gray-400 font-normal">({currentReviewsCount} avis)</span>
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                        <span>{shop.location?.city || "Douala"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CTA Direct */}
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            {shop.phone && (
                                <a 
                                    href={`tel:${shop.phone}`}
                                    className="flex-1 sm:flex-none px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 transition"
                                >
                                    <Phone className="w-3.5 h-3.5" />
                                    Contacter le vendeur
                                </a>
                            )}
                            <button onClick={() => setReviewModal(true)} className="px-3 py-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-medium rounded-lg transition">
                                Donner un avis
                            </button>
                        </div>
                    </div>

                    {/* Navigation par Onglets */}
                    <div className="flex border-t border-gray-100 mt-4 -mx-4 px-4 pt-2">
                        <button 
                            onClick={() => setActiveTab("products")}
                            className={`flex items-center gap-1.5 pb-2 pt-1 text-xs font-bold border-b-2 px-2 transition-all ${
                                activeTab === "products" 
                                    ? "border-orange-500 text-orange-600" 
                                    : "border-transparent text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            <ShoppingBag className="w-3.5 h-3.5" />
                            Produits ({products.length})
                        </button>
                        <button 
                            onClick={() => setActiveTab("about")}
                            className={`flex items-center gap-1.5 pb-2 pt-1 text-xs font-bold border-b-2 px-2 transition-all ${
                                activeTab === "about" 
                                    ? "border-orange-500 text-orange-600" 
                                    : "border-transparent text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            <Info className="w-3.5 h-3.5" />
                            Infos & Description
                        </button>
                    </div>
                </div>
            </div>

            {/* Zone de Contenu Dynamique */}
            <div className="container mx-auto px-2 sm:px-4 py-6">
                {activeTab === "products" ? (
                    <>
                        {loadingProducts ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-lg h-56 animate-pulse border border-gray-200" />
                                ))}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                                <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                <p className="text-gray-500 text-xs">Aucun article en vente dans cette boutique.</p>
                            </div>
                        ) : (
                            <ProductsList
                                products={products} 
                                layout="grid"
                                showShopName={false}
                                showShopAddress={false}
                                showDescription={false}
                            />
                        )}
                    </>
                ) : (
                    <div className="bg-white rounded-xl p-4 border border-gray-200 max-w-2xl">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">À propos de nous</h3>
                        <p className="text-gray-600 text-xs leading-relaxed whitespace-pre-line">
                            {shop.description || "Aucune description fournie par le vendeur."}
                        </p>
                        {shop.phone && (
                            <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                                <strong>Téléphone direct :</strong> {shop.phone}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ReviewModal 
                title="Donnez votre avis"
                isOpen={reviewModal}
                onClose={() => setReviewModal(false)}
                onSubmit={handleSubmit}
                icon={<Star />}
            />
        </div>
    );
}